/**
 * HeroMedia — the login page's background image, GIF or short video.
 *
 * 2026-09-04 — owner: "אני רוצה שיתמוך גם בקבצי GIF או וידאו קצרים".
 *
 * One component, used by BOTH the real login screen and the admin
 * preview, so what the designer shows is what users get. The element
 * always carries the `hero-image` class: every sizing rule built by
 * buildLoginCss (object-fit, object-position, zoom) applies to <video>
 * exactly as it does to <img>, so the layout controls keep working
 * without a second set of CSS to maintain.
 *
 * Animated GIF/WebP need nothing special — <img> plays them. Only real
 * video containers get a <video>.
 *
 * Two constraints browsers impose, encoded here rather than left for
 * someone to rediscover:
 *
 *  1. Autoplay is only permitted while MUTED. `muted` + `playsInline`
 *     are not styling choices, they are the price of autoplay, so a
 *     login video is always silent.
 *  2. prefers-reduced-motion is a real accessibility setting. When it is
 *     on we show the poster still instead of a moving background — on
 *     the one screen a user cannot skip.
 */

import React, { useEffect, useState } from 'react';
import { isVideoUrl } from './loginDesign';

const hide = (e) => { e.target.style.visibility = 'hidden'; };

const HeroMedia = ({ design, resolveUrl, onLoad }) => {
  const url = design.heroImageUrl;
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduceMotion(mq.matches);
    apply();
    // Safari < 14 only has the deprecated listener API.
    if (mq.addEventListener) {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);

  if (!url) return null;

  const poster = design.heroPosterUrl ? resolveUrl(design.heroPosterUrl) : '';

  if (isVideoUrl(url)) {
    // Reduced motion: fall back to the poster still. With no poster
    // configured we keep the video element but leave it paused on its
    // first frame rather than showing an empty box.
    if (reduceMotion && poster) {
      return <img src={poster} alt="" className="hero-image" onLoad={onLoad} onError={hide} />;
    }
    return (
      <video
        className="hero-image"
        src={resolveUrl(url)}
        poster={poster || undefined}
        autoPlay={!reduceMotion}
        loop={design.heroVideoLoop !== false}
        muted
        playsInline
        preload="auto"
        onLoadedData={onLoad}
        onError={hide}
      />
    );
  }

  return <img src={resolveUrl(url)} alt="" className="hero-image" onLoad={onLoad} onError={hide} />;
};

export default HeroMedia;
